import React from 'react';
import { useDroppable } from '@dnd-kit/react';
import { CollisionPriority } from '@dnd-kit/abstract';
import { useTranslation } from 'common/i18n/hooks';
import { TranslationKey } from 'common/i18n';
import { ExperimentMetric, ExperimentMetricType } from 'apps/experiments/src/types';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { EMPTY_METRIC_ID, MetricResults, useExperimentsStore } from 'apps/experiments/src/store';
import { TrackingEventSource } from 'apps/experiments/src/components/tracking';
import { useExperimentMetrics } from 'apps/experiments/src/components/hooks/metrics';
import { SORTABLE_METRIC_TYPE } from '../const';
import styles from './index.module.styl';
import { ExperimentPageSummaryMetricsCategoryHeader } from './_header';
import { ExperimentPageSummaryMetricsCategoryRow } from './_row';
type ExperimentPageSummaryMetricsCategoryProps = {
  header: TranslationKey;
  metrics: ExperimentMetric[];
  variants: string[];
  absMax: number;
  type: ExperimentMetricType;
  results: MetricResults;
};
export function ExperimentPageSummaryMetricsCategory(props: ExperimentPageSummaryMetricsCategoryProps) {
  const {
    t
  } = useTranslation();
  const {
    metrics,
    createMetric
  } = useExperimentMetrics();
  const {
    ref
  } = useDroppable({
    id: props.type,
    type: "metric-group",
    accept: SORTABLE_METRIC_TYPE,
    collisionPriority: CollisionPriority.Low
  });
  const emptyMetricPresent = metrics.filter(metric => metric.id === EMPTY_METRIC_ID && metric.type === props.type).length > 0;
  const onAddMetric = React.useCallback(() => {
    createMetric(props.type, undefined, undefined, TrackingEventSource.ExperimentPageMetrics);
  }, [createMetric, props.type]);
  let variants = [...props.variants];
  if (!variants.length) {
    variants = [...variants, "placeholder"];
  }
  let addMetricTooltip = undefined;
  if (emptyMetricPresent) {
    addMetricTooltip = {
      text: t("experiments.actions.empty-metric-present")
    };
  }
  const AddMetricBtn = React.useCallback((btnProps: {
    children?: React.ReactNode;
    className?: string;
    theme?: ButtonTheme;
  }) => {
    return <ButtonReact {...btnProps} size={ButtonSize.Medium} icon={"plus"} disabled={emptyMetricPresent} tooltip={addMetricTooltip} onClick={ev => {
      ev.stopPropagation();
      onAddMetric();
    }} />;
  }, [emptyMetricPresent, addMetricTooltip, onAddMetric]);
  return <div className={styles.category} data-sentry-component="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx">
      <div className={styles.header}>
        <span>{t(props.header)}</span>
        <AddMetricBtn className={styles.addMetricBtn} theme={ButtonTheme.Tertiary} data-sentry-element="AddMetricBtn" data-sentry-source-file="index.tsx">
          {t("common.add")}
        </AddMetricBtn>
      </div>
      <ExperimentPageSummaryMetricsCategoryHeader absMax={props.absMax} multiVariant={variants.length > 1} data-sentry-element="ExperimentPageSummaryMetricsCategoryHeader" data-sentry-source-file="index.tsx" />
      <div ref={ref} className={styles.rowContainer}>
        <Content metrics={props.metrics} variants={variants} type={props.type} absMax={props.absMax} results={props.results} addMetricBtn={<AddMetricBtn theme={ButtonTheme.Secondary}>
              {t("experiments.main.metrics.add-metric", {
          metricType: t(props.header)
        })}
            </AddMetricBtn>} data-sentry-element="Content" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}
function Content(props: Omit<ExperimentPageSummaryMetricsCategoryProps, `header`> & {
  addMetricBtn?: React.ReactNode;
}) {
  var _props$metrics;
  const {
    t
  } = useTranslation();
  const metricsSearch = useExperimentsStore(state => state.metricsSearch);
  const setMetricsSearch = useExperimentsStore(state => state.setMetricsSearch);
  if ((_props$metrics = props.metrics) !== null && _props$metrics !== void 0 && _props$metrics.length) {
    return props.metrics.map((metric, metricIndex) => <ExperimentPageSummaryMetricsCategoryRow key={metric.id} index={metricIndex} metric={metric} variantNames={props.variants} type={props.type} absMax={props.absMax} results={props.results[metric.id]} />);
  }
  if (metricsSearch) {
    return <div className={styles.emptyState}>
        <div className={styles.emptyStateMessage}>
          {t("experiments.main.metrics.no-results", {
          searchTerm: metricsSearch
        })}
        </div>
        <ButtonReact theme={ButtonTheme.Secondary} onClick={() => setMetricsSearch("")}>
          {t("experiments.main.metrics.clear-search")}
        </ButtonReact>
      </div>;
  }
  return <div className={styles.emptyState} data-sentry-component="Content" data-sentry-source-file="index.tsx">{props.addMetricBtn}</div>;
}